import { module as BaseModule } from "modujs"
import Swiper from "swiper"
import { Autoplay, Pagination, Navigation } from "swiper/modules"
import gsap from "gsap"
import { Observer } from "gsap/Observer"

import "swiper/css" //eslint-disable-line
import "swiper/css/navigation" //eslint-disable-line
import "swiper/css/pagination" //eslint-disable-line

gsap.registerPlugin(Observer)

export default class extends BaseModule {
  init() {
    const slider = this.el
    const { sliderConfig } = slider.dataset

    const params = {
      owners: {
        draggable: true,
        grabCursor: true,
        slidesPerView: 1.15,
        spaceBetween: 1.125 * 16,
        breakpoints: {
          768: {
            slidesPerView: 1.8,
            spaceBetween: 1.5 * 16,
          },
          1280: {
            slidesPerView: 2.2,
          },
        },
        on: {
          init: this.equalizeSlideHeights,
          slideChange: this.equalizeSlideHeights,
          update: this.equalizeSlideHeights,
          afterInit: this.equalizeSlideHeights,
        },
      },
      frontpage: {
        modules: [Navigation],
        draggable: true,
        grabCursor: true,
        slidesPerView: 1.15,
        autoHeight: true,
        spaceBetween: 1.125 * 16,
        navigation: {
          nextEl: ".button-next",
          prevEl: ".button-prev",
        },
        on: {
          init: this.equalizeSlideHeights,
          slideChange: this.equalizeSlideHeights,
          update: this.equalizeSlideHeights,
          afterInit: this.equalizeSlideHeights,
        },
        breakpoints: {
          768: {
            slidesPerView: 2,
            spaceBetween: 1.5 * 16,
            autoHeight: false,
          },
          1280: {
            slidesPerView: 2.5,
          },
        },
      },
      team: {
        modules: [Navigation],
        navigation: {
          nextEl: document.querySelector(".button-next"),
          prevEl: document.querySelector(".button-prev"),
        },
        draggable: true,
        grabCursor: true,
        slidesPerView: 1.25,
        spaceBetween: 1.125 * 16,
        breakpoints: {
          768: {
            slidesPerView: 1.2,
            spaceBetween: 1.5 * 16,
          },
          1280: {
            slidesPerView: 1.6,
          },
        },
        on: {
          init: this.equalizeSlideHeights,
          slideChange: this.equalizeSlideHeights,
          update: this.equalizeSlideHeights,
          afterInit: this.equalizeSlideHeights,
        },
      },
      space: {
        modules: [Navigation],
        draggable: true,
        grabCursor: true,
        slidesPerView: "auto",
        spaceBetween: 1.125 * 16,
        navigation: {
          nextEl: ".button-next",
          prevEl: ".button-prev",
        },
        breakpoints: {
          768: {
            spaceBetween: 1.5 * 16,
          },
        },
      },
      spaceClone: {
        modules: [Navigation],
        draggable: true,
        grabCursor: true,
        slidesPerView: "auto",
        spaceBetween: 1.125 * 16,
        navigation: {
          nextEl: ".button-next-clone",
          prevEl: ".button-prev-clone",
        },
        breakpoints: {
          768: {
            spaceBetween: 1.5 * 16,
          },
        },
        on: {
          slideChange: () => {
            const current = document.querySelector(
              "[data-slider-clone-current]"
            )
            const altElement = document.querySelector("[data-slider-clone-alt]")
            const altDescription =
              this.swiper.slides[this.swiper.realIndex].querySelector("img")
                ?.alt ||
              this.swiper.slides[this.swiper.realIndex].querySelector("video")
                ?.dataset.alt ||
              ""

            altElement.innerHTML = altDescription

            const currentIndex = this.swiper.realIndex + 1
            current.innerHTML = `${currentIndex.toString().padStart(2, "0")}`
          },
        },
      },
      cover: {
        modules: [Autoplay, Pagination],
        draggable: true,
        slidesPerView: 1,
        grabCursor: true,
        speed: 800,
        pagination: {
          el: ".swiper-pagination",
          clickable: true,
        },
        autoplay: {
          delay: 5000,
          disableOnInteraction: false,
        },
        loop: true,
        on: {
          init() {
            this.timelines = []
            this.slides.forEach((slide, i) => {
              const imgs = slide.querySelectorAll("img")

              this.timelines[i] = gsap
                .timeline({ paused: false })
                .set(imgs, {
                  scale: 1.1,
                  translateX: "5%",
                  filter: "blur(10px)",
                })
                .to(imgs, {
                  translateX: "0%",
                  filter: "blur(0px)",
                  duration: 1,
                })
                .to(imgs, { scale: 1, duration: 2 })
            })
          },
          autoplayTimeLeft(s, time, progress) {
            if (!this.timelines) return

            const timescale = 1 - progress
            const { bullets } = s.pagination

            bullets.forEach((bullet, index) => {
              if (index !== s.realIndex) {
                bullet.style.setProperty("--timescale", 0)
              }

              if (index === s.realIndex) {
                bullet.style.setProperty("--timescale", timescale)
                this.timelines[index].progress(timescale)
              }
            })
          },
        },
      },
      basic: {
        modules: [Pagination],
        draggable: true,
        slidesPerView: 1,
        grabCursor: true,
        pagination: {
          el: ".swiper-pagination",
          clickable: true,
        },
        loop: true,
      },
    }

    if (!params[sliderConfig].on) {
      params[sliderConfig].on = {}
    }

    this.swiper = new Swiper(slider, params[sliderConfig])
    gsap.to(slider, {
      opacity: 1,
      duration: 3,
      ease: "power2.Out",
    })

    const images = this.el.querySelectorAll("img")
    images.forEach((image) => image.classList.remove("hidden"))

    if (sliderConfig === "space") {
      const follower = document.querySelector("[data-slider-follower]")

      const xTo = gsap.quickTo(follower, "x", { duration: 0.05 })
      const yTo = gsap.quickTo(follower, "y", { duration: 0.05 })

      Observer.create({
        target: window,
        type: "pointer",
        onMove: ({ x, y }) => {
          xTo(x)
          yTo(y)
        },
      })

      let tl

      const toggleCursor = () => {
        document.body.classList.toggle("no-cursor", tl.isActive())
      }

      gsap.set(follower, { autoAlpha: 0 })

      tl = gsap
        .timeline({ paused: true })
        .call(toggleCursor)
        .to(follower, { autoAlpha: 1 })

      const slides = this.swiper.slides
      slides.forEach((slide, index) => {
        slide.addEventListener("click", () => {
          const event = new CustomEvent("slide-click", {
            detail: {
              index: index,
            },
          })
          document.dispatchEvent(event)
        })

        slide.addEventListener("mouseenter", () => {
          tl.play()
        })

        slide.addEventListener("mouseleave", () => {
          tl.reverse()
        })
      })

      document.addEventListener("slide-click", (e) => {
        tl.reverse()
      })
    }

    if (sliderConfig === "spaceClone") {
      const clone = document.querySelector("[data-slider-clone]")
      const close = clone.querySelector("[data-slider-clone-close]")
      const tl = gsap.timeline({ paused: true }).set(clone, { display: "flex" })

      document.addEventListener("slide-click", (e) => {
        tl.play()
        this.swiper.update()
        this.swiper.slideTo(e.detail.index)
      })

      close.addEventListener("click", () => {
        tl.reverse()
      })
    }
  }

  /**
   * Equalizes all slides to match the tallest one.
   */
  equalizeSlideHeights() {
    const slides = this.slides
    let maxHeight = 0

    slides.forEach((slide) => {
      slide.style.minHeight = "auto"
    })

    slides.forEach((slide) => {
      maxHeight = Math.max(maxHeight, slide.offsetHeight)
    })

    slides.forEach((slide) => {
      slide.style.minHeight = `${maxHeight}px`
    })
  }
}
